import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { wordDiff } from "./wordDiff";
import type { Draft, Version } from "./types";

const removedClass = "bg-destructive-soft text-destructive";
const addedClass = "bg-success-soft text-success no-underline";

/** Word diff of an older version against the current text, shown above the draft. */
export const VersionCompare = ({
  draft,
  version,
  onClose,
  onRestore,
}: {
  draft: Draft;
  version: Version;
  onClose: () => void;
  onRestore: (nr: number) => Promise<void>;
}) => {
  const [busy, setBusy] = useState(false);
  const diff = useMemo(
    () =>
      version.text !== undefined
        ? wordDiff(version.text, draft.entwurf ?? "")
        : [],
    [version, draft.entwurf],
  );

  const restore = async () => {
    setBusy(true);
    try {
      await onRestore(version.nr);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-primary/40">
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 text-sm">
            <span className="font-medium">
              Fassung {version.nr} im Vergleich zur aktuellen Fassung{" "}
              {draft.aktuelle_nr}
            </span>
            {version.prompt && (
              <p className="mt-1 text-muted-foreground">
                Anweisung: „{version.prompt}“
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={restore} disabled={busy}>
              {busy ? "Wird zurückgeholt …" : `Zu Fassung ${version.nr} zurück`}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Schließen
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          <del className={removedClass}>Rot</del> steht nur in Fassung{" "}
          {version.nr}, <ins className={addedClass}>grün</ins> nur in der
          aktuellen. Zurückholen legt eine neue Fassung an, keine geht verloren.
        </p>
        <pre className="max-h-[32rem] overflow-y-auto whitespace-pre-wrap font-sans text-sm leading-relaxed">
          {diff.map((part, i) =>
            part.kind === "same" ? (
              <span key={i}>{part.text}</span>
            ) : part.kind === "removed" ? (
              <del key={i} className={removedClass}>
                {part.text}
              </del>
            ) : (
              <ins key={i} className={addedClass}>
                {part.text}
              </ins>
            ),
          )}
        </pre>
      </CardContent>
    </Card>
  );
};
